import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { SetupFee_DefaultsResponseDados } from '../contracts/SetupFee_DefaultsResponse'
import type { SetupFee_DefaultRow } from '../contracts/SetupFee_DefaultRow'
import type { SetupFee_StatusResponseDados } from '../contracts/SetupFee_StatusResponse'
import type { SetupFee_EmitInput } from '../contracts/SetupFee_EmitInput'
import type { SetupFee_EmitResponseDados } from '../contracts/SetupFee_EmitResponse'
import type { SetupFee_MarkPaidInput } from '../contracts/SetupFee_MarkPaidInput'
import type { SetupFee_ExemptInput } from '../contracts/SetupFee_ExemptInput'
import type { SetupFee_RerequireInput } from '../contracts/SetupFee_RerequireInput'
import type { SetupFee_DefaultInput } from '../contracts/SetupFee_DefaultInput'
import type { SetupFee_ObligationResponseDados } from '../contracts/SetupFee_ObligationResponse'


// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/SetupFee_DefaultsResponse'
import '../contracts/SetupFee_DefaultRow'
import '../contracts/SetupFee_DefaultInput'

import '../contracts/SetupFee_StatusResponse'
import '../contracts/SetupFee_EmitInput'
import '../contracts/SetupFee_EmitResponse'
import '../contracts/SetupFee_MarkPaidInput'
import '../contracts/SetupFee_ExemptInput'
import '../contracts/SetupFee_RerequireInput'
import '../contracts/SetupFee_ObligationResponse'

/**
 * useSetupFee — SOLE network owner for subscriptions/setup-fee/* staff endpoints.
 *
 * Staff (Full-only): emit, mark-paid, exempt, rerequire, status, defaults.
 * Franqueado checkout is split into useSetupFeeCheckout.ts.
 *
 * All requests go to `config.api.sistemaHost` (app-api-service).
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const defaultsList = ref<SetupFee_DefaultRow[]>([])
const loadingDefaults = ref(false)

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

function generateRequestId(): string {
  return 'setup-req-' + (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now())
}

export function useSetupFee() {
  /**
   * fetchDefaults — GET subscriptions/setup-fee/defaults.
   * Populates the defaultsList ref.
   */
  async function fetchDefaults(): Promise<SetupFee_DefaultRow[]> {
    loadingDefaults.value = true
    try {
      const raw = await httpService.get(
        sistemaUrl('subscriptions/setup-fee/defaults'),
        { responseContract: 'SetupFee_DefaultsResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: SetupFee_DefaultsResponseDados }
      defaultsList.value = resp.dados?.defaults ?? []
      log('useSetupFee', 'fetchDefaults', 'Defaults carregados', { count: defaultsList.value.length })
      return defaultsList.value
    } catch (err) {
      log('useSetupFee', 'fetchDefaults', 'Erro ao carregar defaults', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loadingDefaults.value = false
    }
  }

  /**
   * fetchStatus — GET subscriptions/setup-fee/status/{id}.
   */
  async function fetchStatus(franquiaId: number): Promise<SetupFee_StatusResponseDados> {
    try {
      const raw = await httpService.get(
        sistemaUrl(`subscriptions/setup-fee/status/${franquiaId}`),
        { responseContract: 'SetupFee_StatusResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: SetupFee_StatusResponseDados }
      log('useSetupFee', 'fetchStatus', 'Status carregado', {
        franquiaId,
        status: resp.dados?.status,
      })
      return resp.dados
    } catch (err) {
      log('useSetupFee', 'fetchStatus', 'Erro ao carregar status', {
        franquiaId,
        message: (err as Error)?.message,
      })
      throw err
    }
  }

  /**
   * emit — POST subscriptions/setup-fee/emit.
   * Binds EXACTLY the SetupFee_EmitInput field names.
   */
  async function emit(
    // request_id is optional at the call seam — this owner generates one when
    // the caller does not carry an idempotency key of its own.
    input: Omit<SetupFee_EmitInput, 'request_id'> & { request_id?: string },
  ): Promise<SetupFee_EmitResponseDados> {
    const body: SetupFee_EmitInput = {
      franquia_id: input.franquia_id,
      setup_price_default_id: input.setup_price_default_id,
      installments: input.installments,
      request_id: input.request_id || generateRequestId(),
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/setup-fee/emit'),
      body,
      {
        inputContract: 'SetupFee_EmitInput',
        responseContract: 'SetupFee_EmitResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'setup_fee_emit_failed',
      },
    )
    const resp = raw as { dados: SetupFee_EmitResponseDados }
    log('useSetupFee', 'emit', 'Cobrança de setup emitida', {
      franquia_id: input.franquia_id,
      charge_id: resp.dados?.charge_id,
    })
    return resp.dados
  }

  /**
   * markPaid — POST subscriptions/setup-fee/mark-paid.
   * Binds EXACTLY the SetupFee_MarkPaidInput field names.
   */
  async function markPaid(input: SetupFee_MarkPaidInput): Promise<SetupFee_ObligationResponseDados> {
    const body: SetupFee_MarkPaidInput = {
      franquia_id: input.franquia_id,
      ...(input.reason != null ? { reason: input.reason } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/setup-fee/mark-paid'),
      body,
      {
        inputContract: 'SetupFee_MarkPaidInput',
        responseContract: 'SetupFee_ObligationResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'setup_fee_mark_paid_failed',
      },
    )
    const resp = raw as { dados: SetupFee_ObligationResponseDados }
    log('useSetupFee', 'markPaid', 'Taxa de setup marcada como paga', {
      franquia_id: input.franquia_id,
    })
    return resp.dados
  }

  /**
   * exempt — POST subscriptions/setup-fee/exempt.
   * Binds EXACTLY the SetupFee_ExemptInput field names.
   */
  async function exempt(input: SetupFee_ExemptInput): Promise<SetupFee_ObligationResponseDados> {
    const body: SetupFee_ExemptInput = {
      franquia_id: input.franquia_id,
      ...(input.reason != null ? { reason: input.reason } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/setup-fee/exempt'),
      body,
      {
        inputContract: 'SetupFee_ExemptInput',
        responseContract: 'SetupFee_ObligationResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'setup_fee_exempt_failed',
      },
    )
    const resp = raw as { dados: SetupFee_ObligationResponseDados }
    log('useSetupFee', 'exempt', 'Taxa de setup isentada', {
      franquia_id: input.franquia_id,
    })
    return resp.dados
  }

  /**
   * rerequire — POST subscriptions/setup-fee/rerequire.
   * Binds EXACTLY the SetupFee_RerequireInput field names. Reason is required.
   */
  async function rerequire(input: SetupFee_RerequireInput): Promise<SetupFee_ObligationResponseDados> {
    const body: SetupFee_RerequireInput = {
      franquia_id: input.franquia_id,
      reason: input.reason,
    }
    const raw = await httpService.post(
      sistemaUrl('subscriptions/setup-fee/rerequire'),
      body,
      {
        inputContract: 'SetupFee_RerequireInput',
        responseContract: 'SetupFee_ObligationResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'setup_fee_rerequire_failed',
      },
    )
    const resp = raw as { dados: SetupFee_ObligationResponseDados }
    log('useSetupFee', 'rerequire', 'Taxa de setup re-exigida', {
      franquia_id: input.franquia_id,
    })
    return resp.dados
  }

  /**
   * saveDefault — POST subscriptions/setup-fee/defaults.
   */
  async function saveDefault(input: SetupFee_DefaultInput): Promise<SetupFee_DefaultRow[]> {
    loadingDefaults.value = true
    try {
      const raw = await httpService.post(
        sistemaUrl('subscriptions/setup-fee/defaults'),
        input,
        {
          inputContract: 'SetupFee_DefaultInput',
          responseContract: 'SetupFee_DefaultsResponse',
          skipErrorDisplay: false,
          throwOnEnvelopeFailure: 'setup_fee_default_save_failed',
        },
      )
      const resp = raw as { dados: SetupFee_DefaultsResponseDados }
      defaultsList.value = resp.dados?.defaults ?? []
      log('useSetupFee', 'saveDefault', 'Default salvo com sucesso', {
        currency: input.currency,
        provider_code: input.provider_code,
      })
      return defaultsList.value
    } catch (err) {
      log('useSetupFee', 'saveDefault', 'Erro ao salvar default', err)
      throw err
    } finally {
      loadingDefaults.value = false
    }
  }

  function _reset(): void {
    defaultsList.value = []
    loadingDefaults.value = false
  }

  return {
    defaultsList,
    loadingDefaults,
    fetchDefaults,
    saveDefault,
    fetchStatus,
    emit,
    markPaid,
    exempt,
    rerequire,
    _reset,
  }
}

